import { resolveElementTree } from "./elements.js";
import { getChrome, getGlobalElements, getPages, getSurfaceCard, getWidgets } from "./document.js";

const CHROME_SURFACES = ["header", "sidebar", "footer", "auth", "wallet", "notice", "casino_launch"];

/** Chrome parts backed by `widgets.*`; surface card is an instance merge, not a separate owner. */
const CHROME_WIDGET_OWNED_SURFACES = new Set([
  "header",
  "sidebar",
  "footer",
  "auth",
  "wallet",
  "notice",
  "casino_launch",
]);

function walkValue(value, visit) {
  if (value == null) return;
  if (Array.isArray(value)) {
    for (const item of value) walkValue(item, visit);
    return;
  }
  if (typeof value !== "object") return;
  visit(value);
  for (const child of Object.values(value)) {
    walkValue(child, visit);
  }
}

export function collectElementReferences(root) {
  const refs = new Set();
  walkValue(root, (node) => {
    if (typeof node.element === "string" && node.element) {
      refs.add(node.element);
    }
  });
  return refs;
}

function emptyConsumers() {
  return { widgets: new Set(), pages: new Set(), surfaces: new Set() };
}

/** Where each template name is referenced (widget / page / chrome surface). */
export function indexElementReferenceConsumers(doc) {
  /** @type {Map<string, { widgets: Set<string>, pages: Set<string>, surfaces: Set<string> }>} */
  const map = new Map();

  function add(ref, kind, id) {
    if (!ref) return;
    if (!map.has(ref)) map.set(ref, emptyConsumers());
    map.get(ref)[kind].add(id);
  }

  for (const [widgetName, spec] of Object.entries(getWidgets(doc) ?? {})) {
    collectElementReferences(spec).forEach((ref) => add(ref, "widgets", widgetName));
    for (const key of Object.keys(spec?.elements ?? {})) {
      add(key, "widgets", widgetName);
    }
  }

  for (const [pageKey, page] of Object.entries(getPages(doc) ?? {})) {
    collectElementReferences(page).forEach((ref) => add(ref, "pages", pageKey));
  }

  for (const surface of CHROME_SURFACES) {
    if (CHROME_WIDGET_OWNED_SURFACES.has(surface)) continue;
    const card = getSurfaceCard(doc, surface);
    if (!card) continue;
    collectElementReferences(card).forEach((ref) => add(ref, "surfaces", surface));
  }

  return map;
}

function ensureWidgetElements(doc, widgetName) {
  if (!doc.widgets) doc.widgets = {};
  if (!doc.widgets[widgetName]) doc.widgets[widgetName] = {};
  const spec = doc.widgets[widgetName];
  if (!spec.elements || typeof spec.elements !== "object") {
    spec.elements = {};
  }
  return spec;
}

function ensurePageElements(doc, pageKey) {
  const chrome = getChrome(doc);
  if (!doc.chrome) doc.chrome = { ...chrome };
  if (!doc.chrome.pages) doc.chrome.pages = { ...getPages(doc) };
  if (!doc.chrome.pages[pageKey]) doc.chrome.pages[pageKey] = {};
  const page = doc.chrome.pages[pageKey];
  if (!page.elements || typeof page.elements !== "object") {
    page.elements = {};
  }
  return page;
}

/**
 * Inline `{ element }` patches inside root `elements` so each catalog node is self-contained.
 * References from widgets and chrome stay as `{ element: name }`.
 * Root templates that widgets and chrome do not reference are removed.
 * Not used on viewer ingest: the saved contract stays the source of truth.
 */
export function collapseGlobalElementPatches(doc) {
  if (!doc || typeof doc !== "object") return;

  const globals = doc.elements;
  if (!globals || typeof globals !== "object") return;

  const errors = [];
  const referenced = collectElementReferences({
    widgets: doc.widgets,
    chrome: doc.chrome,
  });

  for (const key of Object.keys(globals)) {
    globals[key] = resolveElementTree(globals[key], globals, errors) ?? globals[key];
  }

  for (const key of Object.keys({ ...globals })) {
    if (!referenced.has(key)) delete globals[key];
  }
}

/**
 * Move a root template onto the single widget or page that references it.
 * Shared templates stay at the root. Not used on viewer ingest.
 */
export function normalizeElementCatalogPlacement(doc) {
  if (!doc?.elements || typeof doc.elements !== "object") return;

  const globals = doc.elements;
  const consumers = indexElementReferenceConsumers(doc);

  for (const [name, template] of Object.entries({ ...globals })) {
    const usage = consumers.get(name);
    if (!usage) continue;

    const widgetOnly = usage.widgets.size > 0 && usage.pages.size === 0 && usage.surfaces.size === 0;
    if (widgetOnly && usage.widgets.size === 1) {
      const widgetName = [...usage.widgets][0];
      const spec = ensureWidgetElements(doc, widgetName);
      if (!spec.elements[name]) {
        spec.elements[name] = template;
      }
      delete globals[name];
      continue;
    }

    const pageOnly = usage.pages.size > 0 && usage.widgets.size === 0 && usage.surfaces.size === 0;
    if (pageOnly && usage.pages.size === 1) {
      const pageKey = [...usage.pages][0];
      const page = ensurePageElements(doc, pageKey);
      if (!page.elements[name]) {
        page.elements[name] = template;
      }
      delete globals[name];
    }
  }
}

/** Every root `elements` key. Widget-local templates stay under each widget. */
export function globalElementNamesForTree(doc) {
  return Object.keys(getGlobalElements(doc));
}

/** Template names referenced by a page but stored at document root (shared catalog blocks). */
export function pageReferencedGlobalTemplates(doc, pageKey) {
  const page = getPages(doc)?.[pageKey];
  if (!page) return [];
  const globals = getGlobalElements(doc);
  const refs = collectElementReferences(page);
  return [...refs].filter((name) => globals[name]).sort();
}
